import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { fetchWithAuth, getProjectStatus } from '../services/api';
import type { JobCosts, Output, ProjectStatusResponse } from '../types';

export const BENCHMARK_MODEL_NAMES: Record<string, string> = {
  'anthropic/claude-3.7-sonnet': 'Claude 3.7 Sonnet',
  'anthropic/claude-sonnet-4.5': 'Claude Sonnet 4.5',
  'openai/gpt-4.1': 'GPT-4.1',
  'google/gemini-2.5-pro': 'Gemini 2.5 Pro',
};

export interface ModelRun {
  projectId: string;
  modelId: string;
  modelName: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  stepCount: number;
  totalSteps: number;
  currentStep: string;
  startTime: number;
  endTime: number | null;
  costs: JobCosts | null;
  outputs: Output[];
  error: string | null;
}

export interface EvaluationScores {
  scores: Record<string, Record<string, number>>;
  winner: string;
  summary: string;
}

export interface OverallRanking {
  rankings: Array<{ model: string; overall_score: number; rank: number }>;
  recommended: string;
  avoid: string | null;
}

export const useBenchmarkStore = defineStore('benchmark', () => {
  const modelRuns = ref<ModelRun[]>([]);
  const framework = ref('');
  const title = ref('');
  const evaluationProjectId = ref<string | null>(null);
  const evaluationStatus = ref<'idle' | 'creating' | 'processing' | 'complete' | 'error'>('idle');
  const evaluationOutputs = ref<Record<string, string>>({});
  const evaluationError = ref<string | null>(null);

  // Human ratings stored in localStorage
  const humanRatings = ref<Record<string, { rating: number; notes: string }>>({});

  let pollIntervals: Record<string, ReturnType<typeof setInterval>> = {};
  const notFoundRetries: Record<string, number> = {};

  // --- Computed ---

  const allModelsComplete = computed(
    () => modelRuns.value.length > 0 && modelRuns.value.every(r => r.status === 'complete' || r.status === 'error')
  );

  const anyModelFailed = computed(() => modelRuns.value.some(r => r.status === 'error'));

  const sessionKey = computed(() => {
    const ids = modelRuns.value
      .map(r => r.projectId)
      .sort()
      .join('_');
    return ids ? `benchmark_ratings_${ids}` : null;
  });

  const evalSessionKey = computed(() => {
    const ids = modelRuns.value
      .map(r => r.projectId)
      .sort()
      .join('_');
    return ids ? `benchmark_eval_${ids}` : null;
  });

  const editorialScores = computed(() =>
    parseEvaluationScores(evaluationOutputs.value['benchmark_editorial_evaluation'])
  );
  const chapterScores = computed(() => parseEvaluationScores(evaluationOutputs.value['benchmark_chapter_evaluation']));
  const revisionScores = computed(() =>
    parseEvaluationScores(evaluationOutputs.value['benchmark_revision_evaluation'])
  );
  const welcomeScores = computed(() => parseEvaluationScores(evaluationOutputs.value['benchmark_welcome_evaluation']));
  const overallRanking = computed(() =>
    parseOverallRanking(evaluationOutputs.value['benchmark_overall_recommendation'])
  );

  // --- Init ---

  async function init(params: {
    projectIds: string[];
    modelIds: string[];
    frameworkId: string;
    manuscriptTitle: string;
  }) {
    framework.value = params.frameworkId;
    title.value = params.manuscriptTitle;

    modelRuns.value = params.projectIds.map((projectId, i) => {
      const modelId = params.modelIds[i] ?? '';
      return {
        projectId,
        modelId,
        modelName: BENCHMARK_MODEL_NAMES[modelId] ?? modelId,
        status: 'pending',
        stepCount: 0,
        totalSteps: 0,
        currentStep: '',
        startTime: Date.now(),
        endTime: null,
        costs: null,
        outputs: [],
        error: null,
      };
    });

    loadHumanRatings();
    loadCachedEvaluation();

    // Start all projects and wait — ensures D1 records exist before polling starts
    // start-free is idempotent: no-op if project is already running
    await Promise.all(
      modelRuns.value.map(run =>
        fetchWithAuth(`/api/projects/${run.projectId}/start-free`, { method: 'POST' }).catch(() => {})
      )
    );

    // Start polling all model projects
    for (const run of modelRuns.value) {
      startPolling(run.projectId);
    }
  }

  function startPolling(projectId: string) {
    // Poll immediately, then every 5 seconds
    pollProject(projectId);
    pollIntervals[projectId] = setInterval(() => pollProject(projectId), 5000);
  }

  async function pollProject(projectId: string) {
    const run = modelRuns.value.find(r => r.projectId === projectId);
    if (!run || run.status === 'complete' || run.status === 'error') return;

    try {
      const data: ProjectStatusResponse = await getProjectStatus(projectId);
      run.status = data.status === 'complete' ? 'complete' : data.status === 'error' ? 'error' : 'processing';

      // Collect outputs from the framework
      const fw = data.frameworks?.find(f => f.frameworkId === framework.value);
      if (fw) {
        run.outputs = fw.outputs ?? [];
        run.totalSteps = fw.totalSteps ?? 0;
        run.stepCount = fw.completedSteps ?? 0;
        run.currentStep = fw.currentSteps?.[0]?.name ?? '';
      }

      if (run.status === 'complete' || run.status === 'error') {
        run.endTime = Date.now();
        clearInterval(pollIntervals[projectId]);
        delete pollIntervals[projectId];

        // Fetch debug info for costs
        if (run.status === 'complete') {
          try {
            const debugRes = await fetchWithAuth(`/api/projects/${projectId}/debug`);
            if (debugRes.ok) {
              const debug = await debugRes.json();
              run.costs = debug.costs ?? null;
            }
          } catch {
            // costs unavailable, not critical
          }
        }

        // Check if all done and trigger evaluation (only if no cached eval exists)
        if (allModelsComplete.value && evaluationStatus.value === 'idle') {
          await createEvaluationProject();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // PROJECT_NOT_FOUND can happen while start-free is still in-flight — retry up to 10 times
      if (msg === 'PROJECT_NOT_FOUND') {
        notFoundRetries[projectId] = (notFoundRetries[projectId] ?? 0) + 1;
        if (notFoundRetries[projectId] < 10) return; // keep polling, don't error yet
      }
      run.status = 'error';
      run.error = msg;
      clearInterval(pollIntervals[projectId]);
      delete pollIntervals[projectId];
    }
  }

  async function createEvaluationProject() {
    evaluationStatus.value = 'creating';
    evaluationError.value = null;

    try {
      const markdown = buildEvaluationManuscript();
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const file = new File([blob], 'benchmark-data.md', { type: 'text/markdown' });

      const formData = new FormData();
      formData.append('manuscript', file);
      formData.append('frameworks', JSON.stringify(['benchmark']));

      const uploadRes = await fetch('/api/projects', { method: 'POST', body: formData });
      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.error ?? 'Failed to create evaluation project');
      }

      const { projectId: pid } = await uploadRes.json();
      evaluationProjectId.value = pid;
      if (evalSessionKey.value) {
        localStorage.setItem(evalSessionKey.value, pid);
      }
      // Store reverse mapping so the edits page can link back to the benchmark URL
      localStorage.setItem(
        `benchmark_context_${pid}`,
        JSON.stringify({
          projectIds: modelRuns.value.map(r => r.projectId),
          models: modelRuns.value.map(r => r.modelId),
          framework: framework.value,
          title: title.value,
        })
      );

      // Start for free — evaluation is part of the benchmark, no additional charge
      const startRes = await fetchWithAuth(`/api/projects/${pid}/start-free`, { method: 'POST' });
      if (!startRes.ok) {
        const err = await startRes.json();
        throw new Error(err.error ?? 'Failed to start evaluation');
      }

      evaluationStatus.value = 'processing';
      startPollingEvaluation(pid);
    } catch (err) {
      evaluationStatus.value = 'error';
      evaluationError.value = err instanceof Error ? err.message : 'Evaluation failed';
    }
  }

  function startPollingEvaluation(projectId: string) {
    let evalPollErrors = 0;

    const poll = async () => {
      try {
        const data: ProjectStatusResponse = await getProjectStatus(projectId);
        evalPollErrors = 0; // reset on success

        if (data.status === 'complete') {
          clearInterval(pollIntervals['evaluation']);
          delete pollIntervals['evaluation'];
          evaluationStatus.value = 'complete';

          // Collect all benchmark outputs
          const fw = data.frameworks?.find(f => f.frameworkId === 'benchmark');
          if (fw) {
            for (const output of fw.outputs ?? []) {
              evaluationOutputs.value[output.name] = output.content;
            }
          }
        } else if (data.status === 'error') {
          clearInterval(pollIntervals['evaluation']);
          delete pollIntervals['evaluation'];
          evaluationStatus.value = 'error';
          evaluationError.value = 'Evaluation processing failed';
        }
      } catch (err) {
        evalPollErrors++;
        const msg = err instanceof Error ? err.message : '';

        // Cached evaluation project no longer exists — clear stale cache and allow a new one
        if (msg === 'PROJECT_NOT_FOUND' || evalPollErrors >= 5) {
          clearInterval(pollIntervals['evaluation']);
          delete pollIntervals['evaluation'];
          if (evalSessionKey.value) {
            localStorage.removeItem(evalSessionKey.value);
          }
          evaluationProjectId.value = null;
          evaluationStatus.value = 'idle';

          // If all models are already done, kick off a fresh evaluation
          if (allModelsComplete.value) {
            await createEvaluationProject();
          }
        }
      }
    };

    poll();
    pollIntervals['evaluation'] = setInterval(poll, 5000);
  }

  // Build a structured markdown document from all model outputs
  function buildEvaluationManuscript(): string {
    const lines: string[] = [
      '# Benchmark Evaluation Data',
      '',
      `**Framework:** ${framework.value}`,
      `**Models tested:** ${modelRuns.value.map(r => r.modelId).join(', ')}`,
      `**Manuscript:** ${title.value || 'Untitled'}`,
      '',
      '---',
      '',
    ];

    // Group outputs by type across models
    const outputNames = new Set<string>();
    for (const run of modelRuns.value) {
      for (const output of run.outputs) {
        outputNames.add(output.name);
      }
    }

    // Friendly names for output sections
    const sectionNames: Record<string, string> = {
      [`${framework.value}_welcome_letter`]: 'Welcome Letter',
      [`${framework.value}_editorial_letter`]: 'Editorial Letter',
      [`${framework.value}_revision_plan`]: 'Revision Plan',
      [`${framework.value}_chapter_notes`]: 'Chapter Notes',
      roast_editorial_letter: 'Editorial Letter',
      roast_chapter_notes: 'Chapter Notes',
    };

    // Output each section with all models' versions
    for (const outputName of outputNames) {
      const sectionTitle = sectionNames[outputName] ?? outputName;
      lines.push(`## ${sectionTitle}`, '');

      for (const run of modelRuns.value) {
        const output = run.outputs.find(o => o.name === outputName);
        if (!output || !output.content) continue;

        lines.push(`### Model: ${run.modelId}`, '', output.content, '', '---', '');
      }
    }

    return lines.join('\n');
  }

  // --- Human ratings ---

  function loadHumanRatings() {
    if (!sessionKey.value) return;
    try {
      const stored = localStorage.getItem(sessionKey.value);
      if (stored) humanRatings.value = JSON.parse(stored);
    } catch {
      // ignore
    }
  }

  function loadCachedEvaluation() {
    if (!evalSessionKey.value) return;
    const pid = localStorage.getItem(evalSessionKey.value);
    if (!pid) return;
    evaluationProjectId.value = pid;
    evaluationStatus.value = 'processing';
    startPollingEvaluation(pid);
  }

  function saveHumanRating(key: string, rating: number, notes: string) {
    humanRatings.value[key] = { rating, notes };
    if (sessionKey.value) {
      localStorage.setItem(sessionKey.value, JSON.stringify(humanRatings.value));
    }
  }

  function exportHumanRatings() {
    const data = {
      title: title.value,
      framework: framework.value,
      models: modelRuns.value.map(r => ({ modelId: r.modelId, modelName: r.modelName })),
      ratings: humanRatings.value,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `benchmark-ratings-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // --- Cleanup ---

  function cleanup() {
    for (const id of Object.keys(pollIntervals)) {
      clearInterval(pollIntervals[id]);
    }
    pollIntervals = {};
  }

  return {
    modelRuns,
    framework,
    title,
    evaluationProjectId,
    evaluationStatus,
    evaluationOutputs,
    evaluationError,
    humanRatings,
    allModelsComplete,
    anyModelFailed,
    editorialScores,
    chapterScores,
    revisionScores,
    welcomeScores,
    overallRanking,
    init,
    saveHumanRating,
    exportHumanRatings,
    cleanup,
  };
});

// --- Parsers ---

function parseEvaluationScores(content: string | undefined): EvaluationScores | null {
  if (!content) return null;
  const match = content.match(/```json\n([\s\S]*?)\n```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1] as string);
    if (parsed.not_applicable) return null;
    return parsed;
  } catch {
    return null;
  }
}

function parseOverallRanking(content: string | undefined): OverallRanking | null {
  if (!content) return null;
  const match = content.match(/```json\n([\s\S]*?)\n```/);
  if (!match) return null;
  try {
    return JSON.parse(match[1] as string);
  } catch {
    return null;
  }
}
